import { useState, useEffect, useRef } from "react";
import { statementsApi, platformApi, arrearsApi, contributionsApi } from "@/lib/api";
import { downloadBlob, openBlob, todayStr } from "@/lib/download";
import { CheckCircle, WarningCircle, FilePdf, Eye, DownloadSimple, WhatsappLogo, UsersThree, HandCoins, TrendUp, TrendDown, Phone, X } from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";
import PlatformKpiDrillDown from "./PlatformKpiDrillDown";

export default function MemberStatement({ onSelectBereavement }) {
  const [data, setData] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [drillTopic, setDrillTopic] = useState(null); // 'members' | 'payouts' | 'income' | null
  const [arrearsOpen, setArrearsOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      statementsApi.my().then(({ data }) => setData(data)),
      platformApi.kpis().then(({ data }) => setKpis(data)).catch(() => setKpis(null)),
    ]).finally(() => setLoading(false));
  }, []);

  const handlePdf = async (mode) => {
    if (!data) return;
    setPdfBusy(true);
    try {
      const res = await statementsApi.myPdfBlob();
      const safeUkc = (data.member?.memberNumber || "member").replace(/\//g, "-");
      const filename = `utheri-statement-${safeUkc}-${todayStr()}.pdf`;
      if (mode === "view") openBlob(res.data);
      else downloadBlob(res.data, filename);
    } catch {
      toast.error("Could not generate PDF — please try again");
    } finally {
      setPdfBusy(false);
    }
  };

  const handleWhatsAppShare = () => {
    if (!data) return;
    const { member, rows, totalOwed, totalPaid } = data;
    const arrears = rows.filter((r) => !r.paid).length;
    const balance = totalPaid - totalOwed;
    const balanceLine = balance >= 0
      ? `*Net surplus:* KES ${balance.toLocaleString()}`
      : `*Outstanding:* KES ${Math.abs(balance).toLocaleString()}`;
    const lines = [
      `*Utheri Wa Kangemi — My Statement*`,
      ``,
      `*Member:* ${member.name}`,
      `*UKC:* ${member.memberNumber || "—"}`,
      ``,
      `Total paid: KES ${totalPaid.toLocaleString()}`,
      `Outstanding: KES ${totalOwed.toLocaleString()} (${arrears} ${arrears === 1 ? "bereavement" : "bereavements"})`,
      `Events to date: ${rows.length}`,
      balanceLine,
      ``,
      `Generated ${format(new Date(), "dd MMM yyyy")}`,
    ];
    const text = encodeURIComponent(lines.join("\n"));
    const url = `https://wa.me/?text=${text}`;
    // open in a new tab; on mobile this redirects into the WhatsApp app
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted rounded-xl animate-pulse" />)}</div>;
  if (!data) return <div className="text-center py-16 text-muted-foreground">Unable to load statement</div>;

  const { member, rows, totalOwed, totalPaid } = data;

  return (
    <div className="space-y-6" data-testid="member-statement">
      {/* ── Utheri Platform KPIs ────────────────────────────────────────── */}
      {kpis && (
        <section data-testid="platform-kpis-section">
          <h2 className="font-heading text-base font-semibold tracking-tight mb-2 text-foreground">Utheri Platform</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <button type="button" onClick={() => setDrillTopic("members")} className="bg-primary/5 border border-primary/20 hover:bg-primary/10 hover:border-primary/40 rounded-xl p-3 text-center transition-all active:scale-95 cursor-pointer" data-testid="kpi-active-members">
              <UsersThree className="w-4 h-4 text-primary mx-auto mb-1" weight="duotone" />
              <p className="text-base sm:text-lg font-bold text-primary font-body">{kpis.activeMembers.toLocaleString()}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Active Members</p>
            </button>
            <button type="button" onClick={() => setDrillTopic("payouts")} className="bg-orange-50 border border-orange-200 hover:bg-orange-100 hover:border-orange-300 rounded-xl p-3 text-center transition-all active:scale-95 cursor-pointer" data-testid="kpi-payout-count">
              <HandCoins className="w-4 h-4 text-orange-700 mx-auto mb-1" weight="duotone" />
              <p className="text-base sm:text-lg font-bold text-orange-700 font-body">{kpis.payoutCount.toLocaleString()}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Total Payouts</p>
            </button>
            <button type="button" onClick={() => setDrillTopic("income")} className="bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 rounded-xl p-3 text-center transition-all active:scale-95 cursor-pointer" data-testid="kpi-total-income">
              <TrendUp className="w-4 h-4 text-emerald-700 mx-auto mb-1" weight="duotone" />
              <p className="text-base sm:text-lg font-bold text-emerald-700 font-body">KES {kpis.totalIncome.toLocaleString()}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Total Income</p>
            </button>
            <button type="button" onClick={() => setDrillTopic("payouts")} className="bg-red-50 border border-red-200 hover:bg-red-100 hover:border-red-300 rounded-xl p-3 text-center transition-all active:scale-95 cursor-pointer" data-testid="kpi-total-payouts">
              <TrendDown className="w-4 h-4 text-red-700 mx-auto mb-1" weight="duotone" />
              <p className="text-base sm:text-lg font-bold text-red-700 font-body">KES {kpis.totalPayouts.toLocaleString()}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Payouts (KES)</p>
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1.5 text-center">Tap any card to see the breakdown</p>
        </section>
      )}

      <PlatformKpiDrillDown topic={drillTopic} onClose={() => setDrillTopic(null)} />

      {/* ── My Statement ─────────────────────────────────────────────────── */}
      <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight">My Statement</h2>
          <p className="text-sm text-muted-foreground mt-1">{member.name} &middot; {member.memberNumber}</p>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          <button
            type="button"
            onClick={handleWhatsAppShare}
            data-testid="statement-whatsapp-share-btn"
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm px-3 py-2 rounded-xl bg-[#25D366] text-white font-medium hover:opacity-90 active:scale-95 transition-all"
            title="Share my statement summary on WhatsApp"
          >
            <WhatsappLogo className="w-4 h-4" weight="fill" /> Share
          </button>
          <button
            type="button"
            onClick={() => handlePdf("view")}
            disabled={pdfBusy}
            data-testid="statement-view-pdf-btn"
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm px-3 py-2 rounded-xl bg-secondary text-secondary-foreground font-medium border border-border hover:bg-secondary/70 active:scale-95 disabled:opacity-60 transition-all"
          >
            <Eye className="w-4 h-4" weight="duotone" /> View PDF
          </button>
          <button
            type="button"
            onClick={() => handlePdf("download")}
            disabled={pdfBusy}
            data-testid="statement-download-pdf-btn"
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm px-3 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:opacity-90 active:scale-95 disabled:opacity-60 transition-all"
          >
            {pdfBusy ? <FilePdf className="w-4 h-4 animate-pulse" /> : <DownloadSimple className="w-4 h-4" weight="bold" />}
            Download
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-xl font-bold text-primary font-body">KES {totalPaid.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Total Paid</p>
        </div>
        <button
          type="button"
          onClick={() => setArrearsOpen(true)}
          data-testid="statement-arrears-kpi"
          className="bg-card border border-border rounded-xl p-4 text-center hover:bg-secondary/40 hover:shadow-md transition-all active:scale-95 cursor-pointer"
        >
          <p className="text-xl font-bold text-accent font-body">KES {totalOwed.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Arrears {totalOwed > 0 && <span className="text-primary font-semibold">· Pay now →</span>}</p>
        </button>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-xl font-bold text-foreground font-body">{rows.length}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Events</p>
        </div>
      </div>

      {/* Rows */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="divide-y divide-border">
          {rows.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">No contributions yet</div>
          ) : rows.map((r) => (
            <button
              key={r.bereavementId}
              type="button"
              onClick={() => onSelectBereavement?.(r.bereavementId)}
              data-testid={`statement-row-${r.bereavementId}`}
              className="w-full px-5 py-4 flex items-center justify-between gap-3 text-left hover:bg-secondary/40 transition-colors active:scale-[0.995] cursor-pointer"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{r.deceasedName}</p>
                <p className="text-xs text-muted-foreground truncate">
                  <span>{r.relationship} &middot; {r.dateOfDeath ? format(new Date(r.dateOfDeath), "dd MMM yyyy") : "—"}</span>
                  {r.paid && (r.reference || r.mpesaRef) && (
                    <span data-testid={`statement-ref-${r.bereavementId}`}>
                      <span className="mx-1 text-muted-foreground/50">·</span>
                      <span className="text-muted-foreground/80">Ref: </span>
                      <span className="font-mono text-muted-foreground/90">{r.reference || r.mpesaRef}</span>
                      {r.paymentMethod && <span className="ml-1 uppercase text-[10px] bg-secondary px-1 py-0.5 rounded">{r.paymentMethod}</span>}
                    </span>
                  )}
                </p>
              </div>
              <div className="text-right shrink-0 flex items-center gap-2">
                <span className="font-body font-semibold text-sm">KES {r.contributionAmount}</span>
                {r.paid ? (
                  <CheckCircle className="w-5 h-5 text-green-600" weight="fill" />
                ) : (
                  <WarningCircle className="w-5 h-5 text-accent" weight="fill" />
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
      </div>
      <PayArrearsDialog open={arrearsOpen} onClose={() => setArrearsOpen(false)} onPaid={async () => {
        // refresh statement after success
        try { const { data: fresh } = await statementsApi.my(); setData(fresh); } catch { /* ignore */ }
      }} />
    </div>
  );
}

// ─── Pay Arrears via M-Pesa STK Push ─────────────────────────────────────────
function PayArrearsDialog({ open, onClose, onPaid }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("select");   // select | waiting | done
  const [waitMsg, setWaitMsg] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPhase("select");
    setBusy(false);
    setLoading(true);
    arrearsApi.mine()
      .then(({ data }) => {
        const r = data?.rows || [];
        setRows(r);
        setChecked(new Set(r.map(x => x.bereavementId)));  // default: select all
      })
      .catch(() => toast.error("Could not load arrears"))
      .finally(() => setLoading(false));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open]);

  if (!open) return null;

  const toggle = (bid) => {
    setChecked(prev => {
      const n = new Set(prev);
      n.has(bid) ? n.delete(bid) : n.add(bid);
      return n;
    });
  };

  const total = rows
    .filter(r => checked.has(r.bereavementId))
    .reduce((s, r) => s + r.contributionAmount, 0);

  const handlePay = async () => {
    if (checked.size === 0) { toast.error("Select at least one bereavement"); return; }
    setBusy(true);
    setPhase("waiting");
    setWaitMsg("Sending M-Pesa prompt to your phone…");
    try {
      const { data } = await arrearsApi.stkPushMulti(Array.from(checked));
      toast.success(data.message || "Check your phone for the M-Pesa prompt");
      // Poll status
      const cid = data.checkoutRequestId;
      let elapsed = 0;
      const intervalMs = 4000;
      const maxMs = 90000;
      pollRef.current = setInterval(async () => {
        elapsed += intervalMs;
        try {
          const { data: s } = await contributionsApi.stkStatus(cid);
          if (s.status === "success") {
            clearInterval(pollRef.current); pollRef.current = null;
            setPhase("done");
            setWaitMsg(`Paid KES ${total.toLocaleString()} for ${checked.size} bereavement(s). Receipt ${s.mpesaReceiptNumber || "—"}`);
            toast.success("Payment successful!");
            onPaid?.();
            setBusy(false);
          } else if (s.status === "failed") {
            clearInterval(pollRef.current); pollRef.current = null;
            toast.error(s.resultDesc || "Payment failed or cancelled");
            setPhase("select");
            setBusy(false);
          }
        } catch { /* keep polling */ }
        if (elapsed >= maxMs && pollRef.current) {
          clearInterval(pollRef.current); pollRef.current = null;
          toast.warning("Still waiting — close and check your statement in a minute.");
          setBusy(false);
        }
      }, intervalMs);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not initiate payment");
      setPhase("select");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" data-testid="pay-arrears-dialog">
      <div className="bg-card w-full sm:max-w-lg sm:rounded-xl rounded-t-2xl border border-border max-h-[90vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg font-semibold">Pay arrears via M-Pesa</h3>
            <p className="text-xs text-muted-foreground">Select bereavements to pay. One STK push will cover the total.</p>
          </div>
          <button onClick={() => { if (pollRef.current) clearInterval(pollRef.current); onClose(); }} className="text-muted-foreground" disabled={phase === "waiting"} data-testid="pay-arrears-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {phase === "done" ? (
          <div className="text-center py-8 space-y-3">
            <CheckCircle className="w-14 h-14 mx-auto text-emerald-600" weight="fill" />
            <p className="text-sm font-semibold">{waitMsg}</p>
            <button onClick={onClose} className="px-5 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold">Close</button>
          </div>
        ) : phase === "waiting" ? (
          <div className="text-center py-8 space-y-3">
            <Phone className="w-14 h-14 mx-auto text-primary animate-pulse" weight="fill" />
            <p className="text-sm font-semibold">{waitMsg}</p>
            <p className="text-xs text-muted-foreground">Don&apos;t close this window — we&apos;ll confirm here as soon as M-Pesa responds.</p>
          </div>
        ) : loading ? (
          <p className="text-center text-sm text-muted-foreground py-8">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">You have no outstanding bereavement contributions. 🎉</p>
        ) : (
          <>
            <div className="bg-card border border-border rounded-xl divide-y divide-border max-h-[50vh] overflow-y-auto">
              {rows.map(r => (
                <label key={r.bereavementId} className="px-4 py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-secondary/30">
                  <div className="flex items-center gap-3 min-w-0">
                    <input
                      type="checkbox"
                      checked={checked.has(r.bereavementId)}
                      onChange={() => toggle(r.bereavementId)}
                      data-testid={`pay-arrears-row-${r.bereavementId}`}
                      className="w-4 h-4"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.deceasedName} <span className="text-muted-foreground font-normal">— {r.relationship}</span></p>
                      <p className="text-[11px] text-muted-foreground">{(r.dateOfDeath || "").slice(0,10)}</p>
                    </div>
                  </div>
                  <p className="text-sm font-bold font-body whitespace-nowrap">KES {r.contributionAmount.toLocaleString()}</p>
                </label>
              ))}
            </div>

            <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-xl px-4 py-3">
              <span className="text-sm font-semibold">Total to pay</span>
              <span className="font-heading text-xl font-bold text-primary" data-testid="pay-arrears-total">KES {total.toLocaleString()}</span>
            </div>

            <button
              onClick={handlePay}
              disabled={busy || total === 0}
              data-testid="pay-arrears-stk-btn"
              className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
            >
              <Phone className="w-4 h-4" weight="fill" /> Pay KES {total.toLocaleString()} via M-Pesa
            </button>
            <p className="text-[11px] text-muted-foreground text-center">An M-Pesa prompt will appear on your phone for the full total.</p>
          </>
        )}
      </div>
    </div>
  );
}
